import React from "react";
import { Link } from "react-router-dom";
import styles from "../styles.module.scss";
import { CampaignSuperAdmin } from "../types";
import {
  CNY_TO_USD,
  formatInstallRate,
  formatOrderType,
  formatThousandths,
} from "../../../utils/formatters";
import StatusBadge from "../../StatusBadge";
import DataTable, { DataColumn } from "../../DataTable";
import ManagerSelect, { managerName } from "../../ManagerSelect";
import { OrderManagers } from "../../ManagerSelect/useOrderManagers";

// Sell minus the contractor's cost in dollars; unknown while the cost is.
const revenueOf = (record: CampaignSuperAdmin) =>
  record.buyingPrice == null
    ? null
    : (record.orderPrice || 0) - record.buyingPrice * CNY_TO_USD;

interface CampaignTableProps {
  data: CampaignSuperAdmin[];
  /** Changes with the page's filters: the table starts again from the top. */
  filtersKey: string;
  /** The page's, so its manager filter sees what is assigned here. */
  managers: OrderManagers;
  isBuyVisible: boolean;
  totals: { sell: number; buy: number };
  totalInstalls: number;
  formatCurrency: (value?: number | null, symbol?: string) => string;
  formatDateCell: (value?: string, fallback?: string) => string;
  getComparableDate: (
    item: CampaignSuperAdmin,
    key: "initialDate" | "endDate",
  ) => number;
  getStoreIcon: (store: string) => React.ReactNode;
  getDetailsPath: (record: CampaignSuperAdmin) => string;
  onUserClick: (userId: number) => void;
}

const CampaignTable: React.FC<CampaignTableProps> = ({
  data,
  filtersKey,
  managers,
  isBuyVisible,
  totals,
  totalInstalls,
  formatCurrency,
  formatDateCell,
  getComparableDate,
  getStoreIcon,
  getDetailsPath,
  onUserClick,
}) => {
  const { orderManager, effectiveManager, assign } = managers;
  const columns: DataColumn<CampaignSuperAdmin>[] = [
    {
      key: "hashId",
      title: "ID",
      width: 96,
      fixed: true,
      // The ID opens the campaign page.
      render: (record) => (
        <Link to={getDetailsPath(record)} className={styles.link}>
          {record.hashId || "—"}
        </Link>
      ),
      sorter: (a, b) => (a.hashId || 0) - (b.hashId || 0),
    },
    {
      key: "manager",
      title: "Manager",
      width: 176,
      mobile: "full",
      // The page is for super admins only, so the manager is always editable.
      render: (record) => (
        <ManagerSelect
          value={orderManager(record)}
          inherited={record.user?.manager}
          onChange={(manager) => assign(record, manager)}
        />
      ),
      sorter: (a, b) =>
        managerName(effectiveManager(a)).localeCompare(
          managerName(effectiveManager(b)),
        ),
    },
    {
      key: "user",
      title: "Email",
      width: 240,
      mobile: "full",
      render: (record) =>
        record.user?.email ? (
          <span
            className={styles.link}
            title={record.user.email}
            onClick={(e) => {
              e.stopPropagation();
              if (record.user?.id) onUserClick(record.user.id);
            }}
          >
            {record.user.email}
          </span>
        ) : (
          "—"
        ),
      sorter: (a, b) =>
        (a.user?.email || "").localeCompare(b.user?.email || ""),
    },
    {
      key: "platform",
      title: "Platform",
      width: 112,
      align: "center",
      render: (record) =>
        record.app?.store ? getStoreIcon(record.app.store) : "—",
      sorter: (a, b) => (a.app?.store || "").localeCompare(b.app?.store || ""),
    },
    {
      key: "app",
      title: "App",
      width: 240,
      mobile: "full",
      render: (record) => record.app?.title || "—",
      sorter: (a, b) => (a.app?.title || "").localeCompare(b.app?.title || ""),
    },
    {
      key: "geo",
      title: "GEO",
      width: 88,
      align: "center",
      render: (record) => record.app?.country?.toUpperCase() || "—",
      sorter: (a, b) =>
        (a.app?.country || "").localeCompare(b.app?.country || ""),
    },
    {
      key: "orderType",
      title: "Type",
      width: 128,
      render: (record) =>
        formatOrderType(record.orderType, record.subtype) || "—",
      sorter: (a, b) =>
        formatOrderType(a.orderType, a.subtype).localeCompare(
          formatOrderType(b.orderType, b.subtype),
        ),
    },
    {
      key: "status",
      title: "Status",
      width: 128,
      mobile: "header",
      render: (record) => <StatusBadge status={record.status} />,
      sorter: (a, b) => (a.status || "").localeCompare(b.status || ""),
    },
    ...(isBuyVisible
      ? [
          {
            key: "buyingPrice",
            title: "Buy",
            width: 120,
            render: (record: CampaignSuperAdmin) =>
              formatThousandths(record.buyingPrice, "¥"),
            sorter: (a: CampaignSuperAdmin, b: CampaignSuperAdmin) =>
              (a.buyingPrice || 0) - (b.buyingPrice || 0),
          },
        ]
      : []),
    {
      key: "orderPrice",
      title: "Sell",
      width: 120,
      render: (record) => formatCurrency(record.orderPrice),
      sorter: (a, b) => (a.orderPrice || 0) - (b.orderPrice || 0),
    },
    ...(isBuyVisible
      ? [
          {
            key: "revenue",
            title: "Revenue",
            width: 120,
            render: (record: CampaignSuperAdmin) =>
              formatThousandths(revenueOf(record)),
            sorter: (a: CampaignSuperAdmin, b: CampaignSuperAdmin) =>
              (revenueOf(a) ?? 0) - (revenueOf(b) ?? 0),
          },
        ]
      : []),
    {
      key: "totalInstalls",
      title: "Installs",
      width: 112,
      render: (record) => record.totalInstalls ?? "—",
      sorter: (a, b) => (a.totalInstalls || 0) - (b.totalInstalls || 0),
    },
    {
      key: "pricePerInstall",
      title: "Price / install",
      width: 152,
      render: (record) => formatInstallRate(record.pricePerInstall),
      sorter: (a, b) => (a.pricePerInstall || 0) - (b.pricePerInstall || 0),
    },
    {
      key: "initialDate",
      title: "Start Date",
      width: 128,
      render: (record) => formatDateCell(record.initialDate, record.createdAt),
      sorter: (a, b) =>
        getComparableDate(a, "initialDate") -
        getComparableDate(b, "initialDate"),
    },
    {
      key: "endDate",
      title: "End Date",
      width: 120,
      render: (record) => formatDateCell(record.endDate),
      sorter: (a, b) =>
        getComparableDate(a, "endDate") - getComparableDate(b, "endDate"),
    },
  ];

  return (
    <DataTable
      id="campaigns-2"
      columns={columns}
      rows={data}
      resetKey={filtersKey}
      cardOrder={["app", "user", "manager"]}
      // The dataset can contain rows with duplicate id/hashId, so the array
      // index is the only guaranteed-unique React key.
      rowKey={(_, index) => index}
      defaultSort={{ key: "initialDate", direction: "desc" }}
      totals={{
        hashId: "Totals",
        orderPrice: formatCurrency(totals.sell),
        totalInstalls,
        buyingPrice: formatThousandths(totals.buy, "¥"),
        revenue: formatThousandths(totals.sell - totals.buy * CNY_TO_USD),
      }}
    />
  );
};

export default CampaignTable;
